import React, { useContext } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Avatar,
  Popover,
  Typography,
  Button,
  useTheme,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { CartContext } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { CHECKOUT_URL } from '../../constants/routerUrl';
import { toBengaliNumber } from '../../utils/bengaliNumbers';

const useStyles = makeStyles((theme) => ({
  popover: {
    background: theme.custom.palette.profilePopColor,
    width: theme.spacing(55),
    borderRadius: theme.shape.borderRadius,
  },
  container: {
    minWidth: 100,
    maxWidth: 450,
    display: 'flex',
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    margin: theme.spacing(1),
    background: theme.palette.background.default,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  userName: {
    ...theme.custom.fontFamily.metropolis,
    fontSize: '1rem',
    fontWeight: 500,
  },
  userEmail: {
    ...theme.custom.fontFamily.roboto,
    fontSize: '0.9rem',
  },
  bar: {
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonCheckout: {
    ...theme.custom.fontFamily.metropolis,
    background: theme.palette.secondary.light,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'none',
  },
  tableRow: {
    backgroundColor: theme.palette.background.highlight,
  },
}));

export default function CartPopover({ anchorEl, isOpen, onClose }) {
  const classes = useStyles();
  const id = isOpen ? 'profile-popover' : undefined;
  const cart = useContext(CartContext);
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate(CHECKOUT_URL);
    setTimeout(() => {
      onClose();
    }, 10);
  };

  return (
    <div>
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: classes.popover,
        }}
      >
        {CartTable(cart.items)}
        {cart.items.length > 0 && (
          <div className={classes.bar}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleCheckout}
            >
              চেকআউট
            </Button>
          </div>
        )}
      </Popover>
    </div>
  );
}

function CartTable(items) {
  const cart = useContext(CartContext);
  const classes = useStyles();
  const theme = useTheme();

  return (
    <>
      {cart.items.length > 0 ? (
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <colgroup>
              <col width="45%" />
              <col width="35%" />
              <col width="10%" />
              <col width="10%" />
            </colgroup>
            <TableHead>
              <TableRow className={classes.tableRow}>
                <TableCell>
                  <Typography variant="h6">প্রোডাক্টের নাম</Typography>
                </TableCell>
                <TableCell align="center" style={{ padding: '8px' }}>
                  <Typography variant="h6">সংখ্যা</Typography>
                </TableCell>
                <TableCell align="center" style={{ padding: '8px' }}>
                  <Typography variant="h6">টাকা</Typography>
                </TableCell>
                <TableCell
                  align="center"
                  style={{ padding: '8px' }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.name} className={classes.tableRow}>
                  <TableCell component="th" scope="item">
                    {item.name}
                  </TableCell>
                  <TableCell align="center" style={{ padding: '2px' }}>
                    <IconButton onClick={() => cart.decrementItem(item)}>
                      <RemoveIcon htmlColor={theme.custom.palette.iconColor} />
                    </IconButton>
                    {toBengaliNumber(item.count)}
                    <IconButton onClick={() => cart.incrementItem(item)}>
                      <AddIcon
                        htmlColor={theme.custom.palette.iconColorSecondary}
                      />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    {toBengaliNumber(item.amount * item.count)}
                  </TableCell>
                  <TableCell align="center" style={{ padding: '2px' }}>
                    <IconButton
                      onClick={() => cart.removeItem(item)}
                      style={{ padding: '8px' }}
                    >
                      <DeleteIcon htmlColor={theme.custom.palette.iconColor} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className={classes.container}>
          <Avatar alt="No items" className={classes.avatar}>
            <SentimentVeryDissatisfiedIcon
              htmlColor={theme.custom.palette.iconColor}
              fontSize="large"
            />
          </Avatar>
          <div className={classes.userInfo}>
            <Typography
              className={classes.userName}
              variant="h6"
              component="span"
              color="textPrimary"
            >
              কার্টে কিছু নেই
            </Typography>
            <Typography
              className={classes.userEmail}
              variant="body1"
              component="span"
              color="textSecondary"
            >
              যোগ করুন?
            </Typography>
          </div>
        </div>
      )}
    </>
  );
}
